import React, { ReactNode, useState } from 'react';
import { leerLocal, escribirLocal, borrarLocal } from '../almacenamientoSeguro';
import { TokenScope } from '../types/auth';
import resolveRealCompanyId from '../utils/resolveRealCompanyId';

interface ContextProps {
	children: React.ReactElement;
}

interface ArcaContextI {
	token: string;
	setToken: (value: string) => void;
	userId: string;
	setUserId: (value: string) => void;
	companyId: string;
	setCompanyId: (value: string) => void;
	userAccountType: string;
	setUserAccountType: (value: string) => void;
	language: string;
	setLanguage: (value: string) => void;
	userScope: TokenScope | null;
	setUserScope: (value: TokenScope | null) => void;
	isMultiCompany: boolean;
	holdingIds: string[];
}

export const ArcaContext = React.createContext({} as ArcaContextI);

export const ArcaProvider = ({ children }: ContextProps) => {
	const hasTokenInLocalStorage = leerLocal('token');
	const hasCompanyIdInLocalStorage = leerLocal('companyId');
	const hasUserIdInLocalStorage = leerLocal('userId');
	const hasUserAccountTypeInLocalStorage = leerLocal('userAccountType');
	const hasLanguageInLocalStorage = leerLocal('language');
	const hasUserScopeInLocalStorage = leerLocal('userScope');

	const [token, setToken] = useState<string>(hasTokenInLocalStorage || '');
	const [userId, setUserId] = useState<string>(hasUserIdInLocalStorage || '');
	// 🔴 Los logins guardaban `"null"` a quien no tiene empresa; leído tal cual es truthy y viaja
	// al backend como si fuese una (product#85). Se normaliza aquí para arreglar también lo ya
	// guardado.
	const [companyId, setCompanyId] = useState<string>(
		resolveRealCompanyId(hasCompanyIdInLocalStorage) ?? ''
	);
	const [userAccountType, setUserAccountType] = useState<string>(
		hasUserAccountTypeInLocalStorage || ''
	);
	const [language, setLanguage] = useState<string>(hasLanguageInLocalStorage || 'es');
	const [userScope, setUserScope] = useState<TokenScope | null>(() => {
		if (!hasUserScopeInLocalStorage) return null;

		try {
			return JSON.parse(hasUserScopeInLocalStorage);
		} catch (error) {
			console.warn('Failed to parse userScope from localStorage:', error);
			// Clear the malformed data from localStorage
			borrarLocal('userScope');
			return null;
		}
	});

	// Computed values for easy access
	const isMultiCompany = userScope?.isMultiCompany || false;
	const holdingIds = userScope?.holdingIds || [];

	const values = {
		companyId,
		setCompanyId,
		userId,
		setUserId,
		token,
		setToken,
		userAccountType,
		setUserAccountType,
		language,
		setLanguage,
		userScope,
		setUserScope,
		isMultiCompany,
		holdingIds
	};

	return (
		<ArcaContext.Provider value={values}>{children as unknown as ReactNode}</ArcaContext.Provider>
	);
};
